import { Component, OnInit } from '@angular/core';
import { DwComponent, DwComponentType, DwQueryFilter, DwQueryFilterBuilderGen, DwQueryFilterGroup, DwRoutingService } from '@devwareapps/devware-cap';
import { FinancialDashboardChart, FinancialDashboardConfig, FinancialDashboardGroup } from '../../models/financial-dashboard-config.model';
import { FinancialsRepositoryService } from '../../services/financials-repository.service';
import { ExaminerRepositoryService } from '../../../examiner/services/examiner-repository.service';
import { AppMetaData, AppMetaDataLookups, AviationTrainingProgramAllItems, CheckrideRequestAggregateBaseQueryEntity, CheckrideRequestAggregateBaseQueryMetaDataAttributes, CheckRideRequestSubStatusAllItems, CheckRideTestTypeAllItems, PilotExaminerEntity } from '../../../../meta-data/app-meta-data.service';
import { ChartDataRequestSeries } from '../../models/chart-data-request.model';
import { DwChartTypes } from '../../../z-devware/visualizations/models/dw-chart-preferences.model';
import { CheckrideFinancialFields } from '../../models/checkride-financial-fields.model';
import { SchoolRepositoryService } from '../../../schools/services/school-repository.service';
import { AdminManagerService } from '../../../shared/services/admin-manager.service';
import { ChartAdditionalFilterOptions } from '../../models/chart-additional-filter.options.model';

@DwComponent({
  key: 'school-financials',
  name: 'School Financials',
  componentType: DwComponentType.pageComponent,
  isGlobal: true
})
@Component({
  selector: 'app-school-financials',
  template: `
    <app-financial-dashboard *ngIf="financialDashboardConfig" [financialDashboardConfig]="financialDashboardConfig" [customFilters]="customFilters" (currentGroupChanged)="setGroup($event)">

      <div secondFilterLine dwRow>
          <div dwCol="3">
           <label class="label">Location</label>
           <dw-search-select-lookup [(ngModel)]="params.flightSchoolBranchId" [paramValues]="lookupParams" (ngModelChange)="updateFilters()" [lookupId]="locationsLookupId"
                showBlankOption="true" blankOptionText="All Locations" ></dw-search-select-lookup>
          </div>
          <div dwCol="3">
            <label class="label">Instructor</label>
            <dw-search-select-lookup [(ngModel)]="params.flightInstructorId" [paramValues]="lookupParams" (ngModelChange)="updateFilters()" [lookupId]="instructorsLookupId"
                showBlankOption="true" blankOptionText="All Instructors" ></dw-search-select-lookup>
          </div>
           <div dwCol="3">
            <label class="label">Examiner</label>
            <dw-search-select-lookup [(ngModel)]="params.pilotExaminerId" [paramValues]="lookupParams" (ngModelChange)="updateFilters()" [lookupId]="examinersBySchoolLookupId"
                showBlankOption="true" blankOptionText="All Examiners" ></dw-search-select-lookup>
          </div>
      </div>
    </app-financial-dashboard>
  `
})
export class SchoolFinancialsComponent implements OnInit {
  protected readonly initialPassRateFilterOptions: ChartAdditionalFilterOptions = {
    includeProgramTypes: [AviationTrainingProgramAllItems.Part141],
    includeTestTypes: [CheckRideTestTypeAllItems.InitialTest]
  };

  protected readonly stageAndEocPassRateFilterOptions: ChartAdditionalFilterOptions = {
    includeProgramTypes: [AviationTrainingProgramAllItems.Part141],
    includeTestTypes: [
      CheckRideTestTypeAllItems.StageCheck1,
      CheckRideTestTypeAllItems.StageCheck2,
      CheckRideTestTypeAllItems.StageCheck3,
      CheckRideTestTypeAllItems.EndOfCourse
    ]
  };

  financialDashboardConfig: FinancialDashboardConfig;
  examiner: PilotExaminerEntity;
  currentGroup: string = 'Not Set';
  fields: CheckrideFinancialFields;
  flightSchoolOrganizationId: number;

  customFilters: DwQueryFilter;

  lookupParams: any[] = [];;

  locationsLookupId = AppMetaDataLookups.FlightSchoolBranchBySchool; // Change to by school
  instructorsLookupId = AppMetaDataLookups.FlightSchoolInstructorBySchool;
  examinersBySchoolLookupId = AppMetaDataLookups.FlightSchoolExaminerBySchool;

  params: {
    flightSchoolBranchId?: number;
    flightInstructorId?: number;
    pilotExaminerId?: number;
  } = {}

  constructor(
    protected financialsRepositoryService: FinancialsRepositoryService,
    protected examinerRepositoryService: ExaminerRepositoryService,
    protected schoolRepositoryService: SchoolRepositoryService,
    protected adminManagerService: AdminManagerService,
    protected dwRoutingService: DwRoutingService
  ) { }

  setGroup(group: string) {
    this.currentGroup = group;
  }

  updateFilters() {
    const filters = new DwQueryFilterBuilderGen<CheckrideRequestAggregateBaseQueryMetaDataAttributes>(new CheckrideRequestAggregateBaseQueryMetaDataAttributes());

    this.customFilters = null;

    const filterGroup = new DwQueryFilterGroup();

    if (this.params.flightSchoolBranchId) {
      filterGroup.AddFilterAnd(filters.Equal(a => a.FlightSchoolBranchId, this.params.flightSchoolBranchId));
    }
    if (this.params.flightInstructorId) {
      filterGroup.AddFilterAnd(filters.Equal(a => a.FlightSchoolInstructorId, this.params.flightInstructorId));
    }
    if (this.params.pilotExaminerId) {
      filterGroup.AddFilterAnd(filters.Equal(a => a.FlightSchoolExaminerId, this.params.pilotExaminerId));
    }

    if (filterGroup.Filters.length > 0) {
      this.customFilters = filterGroup;
    }

  }

  ngOnInit(): void {
    this.fields = this.financialsRepositoryService.getFinancialFields();

    this.revenueField = this.fields.paidRevenue;

    this.setupDashboard();
  }

  revenueField: string;

  setRevenueField() {
    this.setupDashboard();
  }

  setupDashboard() {
    const pilexosUser = this.adminManagerService.getPilexosUser();

    this.lookupParams.push(pilexosUser.flightSchoolOrganizationId);

    this.financialDashboardConfig = {
      contentConfigKey: 'school-financials',
      staticFilter: {
        flightSchoolOrganizationId: pilexosUser?.flightSchoolOrganizationId || -1,
        //pilotExaminerId: this.examiner?.PilotExaminerId || -1,
      },
      groups: [
        this.buildCheckrideGroup(),
        this.buildCheckrideStatGroup(),
        // this.buildInitialPassRateGroup(),
        // this.buildStageAndEocPassRateGroup(),
      ]
    }
  }

  buildCheckrideGroup(): FinancialDashboardGroup {
    const group: FinancialDashboardGroup = {
      groupName: 'Checkrides',
      filterOptions: {
        showYear: true,
        showMonth: true,
        showPilotExaminer: false
      },
      gridConfigKey: 'school-admin-financials',
      charts: [],

    }


    group.charts.push(this.createCheckrideChart('Checkrides', true, true));
    group.charts.push(this.createCheckrideWaitTimesChart('Checkride Wait Times'));

    const fields = this.financialsRepositoryService.getFinancialFields();


    const headerColor = 'blue';

    group.tiles = [
      { headerTemplate: '[year] Checkrides', type: 'year', isCurrency: false, iconClass: 'fa fa-plane dw-blue', additionalText: '', dataField: fields.checkrideCount, chartDataIndex: 0, toolTip: 'Checkrides', hideIfNull: false, moreTileOptions: { headerColor: headerColor } },
      { headerTemplate: '[year] Passed', type: 'year', isCurrency: false, iconClass: 'fa fa-plane-departure dw-green', additionalText: '', dataField: fields.passedCount, chartDataIndex: 0, toolTip: 'Passed', hideIfNull: false, moreTileOptions: { headerColor: headerColor } },
      { headerTemplate: '[year] Failed', type: 'year', isCurrency: false, iconClass: 'fa fa-plane-arrival dw-red', additionalText: '', dataField: fields.failedCount, chartDataIndex: 0, toolTip: 'Failed', hideIfNull: false, moreTileOptions: { headerColor: headerColor } },
      { headerTemplate: '[year] Pass Rate', type: 'year', isCurrency: false, iconClass: 'fa fa-plane dw-blue', additionalText: '', getValue: (data) => this.calcPassRate(data), formatValue: (value) => `${value}%`, dataField: fields.failedCount, chartDataIndex: 0, toolTip: 'Pass Rate', hideIfNull: false, moreTileOptions: { headerColor: headerColor } },
    ];

    return group;
  }

  protected calcPassRate(data: any): number {
    const totalCount = data.PassedCount + data.FailedCount;

    const passRate = totalCount > 0 ? Math.round((data.PassedCount / totalCount) * 100) : 0;

    return passRate;
  }

  protected buildInitialPassRateGroup(): FinancialDashboardGroup {
    return this.buildPassRateGroup('Initial', this.initialPassRateFilterOptions, false);
  }

  protected buildStageAndEocPassRateGroup(): FinancialDashboardGroup {
    return this.buildPassRateGroup('Stages and EOC', this.stageAndEocPassRateFilterOptions, true);
  }

  protected buildPassRateGroup(groupName: string, filterOptions: ChartAdditionalFilterOptions, includeExamTypeChart: boolean): FinancialDashboardGroup {
    const fields = this.financialsRepositoryService.getFinancialFields();
    const headerColor = 'blue';

    const group: FinancialDashboardGroup = {
      groupName: groupName,
      filterOptions: {
        showYear: true,
        showMonth: true,
        showPilotExaminer: false
      },
      gridConfigKey: 'school-admin-financials',
      summaryAdditionalFilterOptions: filterOptions,
      charts: [
        this.createPassRateTrendChart(`${groupName} Pass Rate Trend`, filterOptions),
        this.createOutcomeChart(`${groupName} Outcomes`, filterOptions),
        this.createBreakdownChart(`${groupName} By Examiner`, a => a.RoCheckrideApplicantInfoPilotExaminerFinalName, filterOptions),
        this.createBreakdownChart(`${groupName} By Instructor`, a => a.RoCheckrideApplicantInfoFlightInstructorFinalName, filterOptions),
        this.createBreakdownChart(`${groupName} By Location`, a => a.FlightSchoolBranchId, filterOptions),
      ],
      tiles: [
        { headerTemplate: '[year] Checkrides', type: 'year', isCurrency: false, iconClass: 'fa fa-plane dw-blue', additionalText: '', dataField: fields.checkrideCount, chartDataIndex: 0, toolTip: 'Checkrides', hideIfNull: false, moreTileOptions: { headerColor: headerColor } },
        { headerTemplate: '[year] Passed', type: 'year', isCurrency: false, iconClass: 'fa fa-plane-departure dw-green', additionalText: '', dataField: fields.passedCount, chartDataIndex: 0, toolTip: 'Passed', hideIfNull: false, moreTileOptions: { headerColor: headerColor } },
        { headerTemplate: '[year] Failed', type: 'year', isCurrency: false, iconClass: 'fa fa-plane-arrival dw-red', additionalText: '', dataField: fields.failedCount, chartDataIndex: 0, toolTip: 'Failed', hideIfNull: false, moreTileOptions: { headerColor: headerColor } },
        { headerTemplate: '[year] Pass Rate', type: 'year', isCurrency: false, iconClass: 'fa fa-plane dw-blue', additionalText: '', getValue: (data) => this.calcPassRate(data), formatValue: (value) => `${value}%`, dataField: fields.failedCount, chartDataIndex: 0, toolTip: 'Pass Rate', hideIfNull: false, moreTileOptions: { headerColor: headerColor } },
      ]
    };

    if (includeExamTypeChart) {
      group.charts.splice(2, 0, this.createBreakdownChart(`${groupName} By Exam Type`, a => a.CheckRideTestTypeId, filterOptions));
    }

    return group;
  }

  protected createPassRateTrendChart(title: string, additionalFilterOptions: ChartAdditionalFilterOptions): FinancialDashboardChart {
    const fields = this.financialsRepositoryService.getFinancialFields();

    const series: ChartDataRequestSeries[] = [
      { dataField: fields.passedCount, legendDisplay: 'Passed', axis: 'y', chartType: DwChartTypes.line },
      { dataField: fields.failedCount, legendDisplay: 'Failed', axis: 'y', chartType: DwChartTypes.line },
    ];

    return {
      chartType: DwChartTypes.bar,
      title: title,
      categoryField: a => fields.monthDisplay,
      categoryDisplayOverride: 'Month',
      additionalGroupingFields: [fields.month, fields.year],
      orderByFields: [fields.year, fields.month],
      additionalFilterOptions: additionalFilterOptions,
      series: series
    };
  }

  protected createOutcomeChart(title: string, additionalFilterOptions: ChartAdditionalFilterOptions): FinancialDashboardChart {
    const fields = this.financialsRepositoryService.getFinancialFields();

    return {
      chartType: DwChartTypes.pie,
      title: title,
      categoryField: a => a.CheckRideResultCheckRideOutcomeId,
      dataField: fields.checkrideCount,
      containerClass: 'tile-50',
      additionalFilterOptions: additionalFilterOptions
    };
  }

  protected createBreakdownChart(title: string, categoryField: any, additionalFilterOptions: ChartAdditionalFilterOptions): FinancialDashboardChart {
    const fields = this.financialsRepositoryService.getFinancialFields();

    return {
      chartType: DwChartTypes.column,
      title: title,
      categoryField: categoryField,
      dataField: fields.checkrideCount,
      hideLegend: true,
      additionalFilterOptions: additionalFilterOptions,
      orderByFields: [{ field: fields.checkrideCount, isDescending: true }]
    };
  }

  protected createCheckrideChart(title: string, includeCounts: boolean, includeRevenue: boolean): FinancialDashboardChart {
    const fields = this.financialsRepositoryService.getFinancialFields();

    const config: FinancialDashboardChart = {
      chartType: DwChartTypes.bar, title: title, categoryField: a => fields.monthDisplay, categoryDisplayOverride: 'Month', additionalGroupingFields: [fields.month, fields.year], orderByFields: [fields.year, fields.month],
      series: []
    };
    const allSeries: ChartDataRequestSeries[] = [
      { dataField: fields.requestedCount, legendDisplay: 'Requested', axis: 'y', chartType: DwChartTypes.line },
      { dataField: fields.bookedCount, legendDisplay: 'Booked', axis: 'y', chartType: DwChartTypes.line },
      //{ dataField: fields.completedCount, legendDisplay: 'Completed', axis: 'y', chartType: DwChartTypes.line },
      { dataField: fields.passedCount, legendDisplay: 'Passed', axis: 'y', chartType: DwChartTypes.line },
      { dataField: fields.failedCount, legendDisplay: 'Failed', axis: 'y', chartType: DwChartTypes.line },
      { dataField: fields.discontinuedCount, legendDisplay: 'Discontinued', axis: 'y', chartType: DwChartTypes.line },
      { dataField: fields.cancelledCount, legendDisplay: 'Cancelled', axis: 'y', chartType: DwChartTypes.line },

      // { dataField: fields.requestedRevenue, legendDisplay: 'Requested $', chartType: DwChartTypes.bar },
      // { dataField: fields.bookedRevenue, legendDisplay: 'Booked $', chartType: DwChartTypes.bar },
      // { dataField: fields.paidRevenue, legendDisplay: 'Paid $', chartType: DwChartTypes.bar },
    ];

    config.series = [...allSeries];
    // if (includeCounts) {
    //   config.series = [...config.series, ...allSeries.slice(0, 4)];
    // }

    // if (includeRevenue) {
    //   config.series = [...config.series, ...allSeries.slice(3, 6)];
    // }

    return config;
  }

  private createCheckrideWaitTimesChart(title: string): FinancialDashboardChart {
    const fields = this.financialsRepositoryService.getFinancialFields();

    const config: FinancialDashboardChart = {
      chartType: DwChartTypes.bar, title: title, categoryField: a => fields.monthDisplay, categoryDisplayOverride: 'Month', additionalGroupingFields: [fields.month, fields.year], orderByFields: [fields.year, fields.month],
      series: [],


    };
    const allSeries: ChartDataRequestSeries[] = [
      { dataField: fields.finalWaitTimeDays, legendDisplay: 'Avg Wait Time', chartType: DwChartTypes.line },
      //{ dataField: fields.currentWaitTimeDays, legendDisplay: 'Current Wait Time', chartType: DwChartTypes.line },

      // { dataField: fields.requestedRevenue, legendDisplay: 'Requested $', chartType: DwChartTypes.bar },
      // { dataField: fields.bookedRevenue, legendDisplay: 'Booked $', chartType: DwChartTypes.bar },
      // { dataField: fields.paidRevenue, legendDisplay: 'Paid $', chartType: DwChartTypes.bar },
    ];

    config.series = [...allSeries];
    // if (includeCounts) {
    //   config.series = [...config.series, ...allSeries.slice(0, 4)];
    // }

    // if (includeRevenue) {
    //   config.series = [...config.series, ...allSeries.slice(3, 6)];
    // }

    return config;
  }

  private buildCheckrideStatGroup(): FinancialDashboardGroup {
    const group: FinancialDashboardGroup = {
      groupName: 'Stats',
      filterOptions: {
        showYear: true,
        showMonth: true,
      },
      gridConfigKey: 'school-admin-financials',
      charts: []
    }

    const fields = this.financialsRepositoryService.getFinancialFields();

    const yearDisplay = '';
    group.charts.push({ chartType: DwChartTypes.column, title: `${yearDisplay} Checkride By Airport`, categoryField: a => a.ScheduledAirportId, dataField: fields.checkrideCount, hideLegend: true, orderByFields: [{ field: fields.checkrideCount, isDescending: true }] });
    group.charts.push({ chartType: DwChartTypes.column, title: `${yearDisplay} Checkride By Instructor`, categoryField: a => a.RoCheckrideApplicantInfoFlightInstructorFinalName, dataField: fields.checkrideCount, hideLegend: true, orderByFields: [{ field: fields.checkrideCount, isDescending: true }] });

    group.charts.push({ chartType: DwChartTypes.column, title: `${yearDisplay} Checkride By Examiner`, categoryField: a => a.RoCheckrideApplicantInfoPilotExaminerFinalName, dataField: fields.checkrideCount, hideLegend: true, orderByFields: [{ field: fields.checkrideCount, isDescending: true }] });

    //group.charts.push({ chartType: DwChartTypes.pie, title: `${yearDisplay} Top Airports`, categoryField: a => a.ScheduledAirportId, dataField: fields.checkrideCount, containerClass: 'tile-50', maxItems: 6 });

    group.charts.push({
      chartType: DwChartTypes.column, title: `${yearDisplay} Checkride Types`, categoryField: a => a.AggrCheckRideTypeDisplay, dataField: fields.checkrideCount, hideLegend: true, containerClass: 'tile-502', orderByFields: [{ field: fields.checkrideCount, isDescending: true }],
      series: [
        // { dataField: fields.checkrideCount, legendDisplay: 'Checkrides',  chartType: DwChartTypes.column,  },
        //   { dataField: fields.bookedRevenue, legendDisplay: 'Booked $', chartType: DwChartTypes.column },
      ]
    });


    // const allSeries: ChartDataRequestSeries[] = [
    //   { dataField: fields.checkrideCount, legendDisplay: 'Checkrides', axis: 'y2', chartType: DwChartTypes.line },
    //   { dataField: fields.bookedRevenue, legendDisplay: 'Booked $', chartType: DwChartTypes.bar },
    //   // { dataField: fields.completedCount, legendDisplay: 'Completed', axis: 'y2', chartType: DwChartTypes.line },
    //   // { dataField: fields.requestedRevenue, legendDisplay: 'Requested $', chartType: DwChartTypes.bar },
    //   // { dataField: fields.bookedRevenue, legendDisplay: 'Booked $', chartType: DwChartTypes.bar },
    //   // { dataField: fields.paidRevenue, legendDisplay: 'Paid $', chartType: DwChartTypes.bar },
    // ];


    // group.charts.push({ chartType: DwChartTypes.bar, title: `${yearDisplay} Checkride Types $`, categoryField: a => a.AggrCheckRideTypeDisplay, dataField: fields.bookedRevenue, hideLegend: true, containerClass: 'tile-502' });
    group.charts.push({ chartType: DwChartTypes.pie, title: `${yearDisplay} Statuses`, categoryField: a => a.AggrCheckRideRequestStatus, dataField: fields.checkrideCount, containerClass: 'tile-100', additionalFilterOptions: { excludeStatuses: [CheckRideRequestSubStatusAllItems.Deleted, CheckRideRequestSubStatusAllItems.Draft] } });
    group.charts.push({ chartType: DwChartTypes.pie, title: `${yearDisplay} Checkride Outcomes`, categoryField: a => a.CheckRideResultCheckRideOutcomeId, dataField: fields.checkrideCount, containerClass: 'tile-50' });
    group.charts.push({ chartType: DwChartTypes.pie, title: `${yearDisplay} Exam Types`, categoryField: a => a.CheckRideTestTypeId, dataField: fields.checkrideCount, containerClass: 'tile-50' });
    group.charts.push({ chartType: DwChartTypes.pie, title: `${yearDisplay} Cancellation Reasons`, categoryField: a => a.CheckRideCancellationTypeId, dataField: fields.checkrideCount, containerClass: 'tile-50', additionalFilterOptions: { includeStatuses: [CheckRideRequestSubStatusAllItems.Cancelled] } });

    //group.charts.push({ chartType: DwChartTypes.pie, title: `${yearDisplay} Program Types`, categoryField: a => a.AvaiationTrainingProgramId, dataField: fields.checkrideCount, containerClass: 'tile-50_new' });

    // group.charts.push({ chartType: DwChartTypes.pie, title: `${yearDisplay} Checkride Outcomes - Part 141`, categoryField: a => a.CheckRideResultCheckRideOutcomeId, dataField: fields.checkrideCount, containerClass: 'tile-50', additionalFilterOptions: { includeProgramTypes: [AviationTrainingProgramAllItems.Part141] } });
    // group.charts.push({ chartType: DwChartTypes.pie, title: `${yearDisplay} Checkride Outcomes - Part 61`, categoryField: a => a.CheckRideResultCheckRideOutcomeId, dataField: fields.checkrideCount, containerClass: 'tile-50', additionalFilterOptions: { includeProgramTypes: [AviationTrainingProgramAllItems.Part61] } });

    //group.charts.push({ chartType: DwChartTypes.pie, title: `${yearDisplay} Statuses`, categoryField: a => a.AggrCheckRideRequestStatus, dataField: fields.checkrideCount, containerClass: 'tile-50' });

    return group;
  }

}
